import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Avatar, 
  TextField, 
  InputAdornment, 
  CircularProgress, 
  Button,
  useTheme,
  useMediaQuery,
  Stack,
  Chip,
  alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  Feedback as FeedbackIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBranch } from '../BranchContext';

const Feedback = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [users, setUsers] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(20);
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();

  useEffect(() => {
    api.get('/all-data')
      .then(res => {
        setUsers(res.data.users || []);
        setFeedbacks(res.data.feedbacks || []);
        setOrders(res.data.orders || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [selectedBranch]);

  const getBranchId = order => (order.BranchId ?? order.branchId)?.toString();
  const getOrderId = order => (order.OrderId ?? order.orderId)?.toString();
  const getUserId = user => (user.Id ?? user.id)?.toString();
  const getFeedbackUserId = fb => (fb.UserId ?? fb.userId)?.toString();
  const getFeedbackOrderId = fb => (fb.OrderId ?? fb.orderId)?.toString();
  const selectedBranchId = (selectedBranch?.Id ?? selectedBranch?.id)?.toString();

  const ordersMap = React.useMemo(() => {
    const map = {};
    orders.forEach(order => {
      map[getOrderId(order)] = order;
    });
    return map;
  }, [orders]);

  const usersWithBranchFeedbacks = users.filter(user => {
    const userFeedbacks = feedbacks.filter(fb => getFeedbackUserId(fb) === getUserId(user));
    return userFeedbacks.some(fb => {
      const order = ordersMap[getFeedbackOrderId(fb)];
      return order && getBranchId(order) === selectedBranchId;
    });
  });

  const filteredUsers = usersWithBranchFeedbacks.filter(user => {
    if (!search) return true;
    const nameMatch = user.FullName?.toLowerCase().includes(search.toLowerCase());
    const emailMatch = user.Email?.toLowerCase().includes(search.toLowerCase());
    return nameMatch || emailMatch;
  });

  const displayedUsers = filteredUsers.slice(0, visibleCount);

  const highlightMatch = (user) => {
    if (!search) return false;
    const nameMatch = user.FullName?.toLowerCase().includes(search.toLowerCase());
    const emailMatch = user.Email?.toLowerCase().includes(search.toLowerCase());
    return nameMatch || emailMatch;
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 2.5, md: 4 } }}>
      {/* ✅ Mobile-Optimized Header */}
      <Stack 
        direction="row" 
        alignItems="center" 
        spacing={{ xs: 1.5, sm: 2 }}
        mb={{ xs: 2.5, sm: 3 }}
      >
        <FeedbackIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: 'primary.main' }} />
        <Typography 
          variant="h4" 
          fontWeight={700}
          sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
        >
          {t('UserFeedbacks') || 'User Feedbacks'}
        </Typography>
        <Chip 
          label={filteredUsers.length} 
          color="primary" 
          size="small"
          sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' } }}
        />
      </Stack>

      {/* ✅ Mobile-Optimized Search */}
      <TextField
        variant="outlined"
        placeholder={t('SearchByNameOrEmail') || 'Search by name or email'}
        value={search}
        onChange={e => setSearch(e.target.value)}
        fullWidth
        size={isMobile ? "small" : "medium"}
        sx={{ 
          mb: { xs: 3, sm: 4 }, 
          maxWidth: { xs: '100%', sm: 500 }
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </InputAdornment>
          ),
        }}
        inputProps={{
          style: {
            fontSize: isMobile ? '0.9rem' : '1rem'
          }
        }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 4, sm: 6 } }}>
          <CircularProgress size={isMobile ? 40 : 50} />
        </Box>
      ) : (
        <>
          {/* ✅ Responsive Grid */}
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            {displayedUsers.map(user => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={user.Id}>
                <Card
                  sx={{ 
                    cursor: 'pointer', 
                    transition: '0.2s',
                    borderRadius: { xs: 2, sm: 3 },
                    border: highlightMatch(user) ? '2px solid' : '1px solid',
                    borderColor: highlightMatch(user) ? 'primary.main' : 'divider',
                    '&:hover': { 
                      boxShadow: 6, 
                      transform: isMobile ? 'none' : 'scale(1.03)',
                      borderColor: 'primary.main'
                    },
                    '&:active': {
                      transform: isMobile ? 'scale(0.98)' : 'scale(1.03)'
                    }
                  }}
                  onClick={() => navigate(`/admin/feedback/${user.Id}`)}
                >
                  <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                    <Stack 
                      direction="row" 
                      alignItems="center" 
                      spacing={{ xs: 1.5, sm: 2 }}
                      mb={{ xs: 1.5, sm: 2 }}
                    >
                      <Avatar 
                        sx={{ 
                          width: { xs: 48, sm: 56 }, 
                          height: { xs: 48, sm: 56 },
                          bgcolor: 'primary.main'
                        }}
                      >
                        <PersonIcon sx={{ fontSize: { xs: 24, sm: 28 } }} />
                      </Avatar>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography 
                          variant="h6" 
                          fontWeight={600}
                          noWrap
                          sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem' } }}
                        >
                          {user.FullName || 'No Name'}
                        </Typography>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          noWrap
                          sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
                        >
                          {user.Email}
                        </Typography>
                      </Box>
                    </Stack>
                    {user.Phone && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: '0.8rem', sm: '0.85rem' },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        📱 {user.Phone}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* ✅ Empty State */}
          {displayedUsers.length === 0 && (
            <Box sx={{ textAlign: 'center', py: { xs: 6, sm: 8 } }}>
              <FeedbackIcon sx={{ fontSize: { xs: 60, sm: 80 }, color: 'grey.300', mb: 2 }} />
              <Typography 
                variant="h6" 
                color="text.secondary"
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
              >
                {search 
                  ? (t('NoUsersFound') || 'No users found') 
                  : (t('NoFeedbacksYet') || 'No feedbacks yet')}
              </Typography>
              {search && (
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mt: 1,
                    fontSize: { xs: '0.8rem', sm: '0.85rem' }
                  }}
                >
                  {t('TryDifferentSearch') || 'Try a different search term'}
                </Typography>
              )}
            </Box>
          )}

          {/* ✅ Mobile-Optimized Show More Button */}
          {visibleCount < filteredUsers.length && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 3, sm: 4 } }}>
              <Button 
                variant="contained" 
                onClick={() => setVisibleCount(visibleCount + 20)}
                size={isMobile ? "medium" : "large"}
                sx={{
                  px: { xs: 3, sm: 4 },
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  borderRadius: 2
                }}
              >
                {t('ShowMore') || 'Show More'} ({filteredUsers.length - visibleCount} {t('remaining') || 'remaining'})
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default Feedback;